import { FC, useEffect, useMemo, useRef } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { allGrades, allSites } from '../../../graphQLOperations/Classes';
import { ClassesAllSitesQuery } from '../../../graphQLOperations/__generated__/ClassesAllSitesQuery.graphql';
import { ClassesAllGradesQuery } from '../../../graphQLOperations/__generated__/ClassesAllGradesQuery.graphql';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { Grade } from '../../../models/Grade';
import { Site } from '../../../models/Site';
import { ClassWizardData } from './wizardTypes';

const SECTION_TOOLTIP =
  'This field is helpful if there are multiple classes with the same name. You can use class period or section number to differentiate between them.';

interface Props {
  data: ClassWizardData;
  onChange: (patch: Partial<ClassWizardData>) => void;
  /** Shows the inline "class already exists" warning when the user tries to continue. */
  duplicateName?: boolean;
}

const ClassWizardDetailsStep: FC<Props> = ({ data, onChange, duplicateName = false }) => {
  const userRedux = useAppSelector(selectUser);
  const sectionInputRef = useRef<HTMLInputElement>(null);

  const sitesData = useLazyLoadQuery<ClassesAllSitesQuery>(allSites, {
    input: { userId: 'UserNode:' + userRedux?.id },
  });
  const gradesData = useLazyLoadQuery<ClassesAllGradesQuery>(allGrades, {});

  const sites = useMemo(() => (sitesData.sites?.nodes ?? []) as Site[], [sitesData]);
  const grades = useMemo(
    () => [...gradesData.allGradeLevels].sort((a, b) => a.order - b.order) as Grade[],
    [gradesData],
  );

  // Pre-load the site when the user only has access to one (see design dev note).
  useEffect(() => {
    if (!data.site && sites.length === 1) {
      onChange({ site: sites[0] });
    }
  }, [sites, data.site, onChange]);

  // Send focus straight to the field that resolves the conflict.
  useEffect(() => {
    if (duplicateName) {
      sectionInputRef.current?.focus();
    }
  }, [duplicateName]);

  return (
    <div className="flex flex-column gap-md py-2">
      <div className="flex flex-column gap-xs">
        <label htmlFor="wizardClassName" className="font-bold">
          Class name <span className="nc_required-mark">*</span>
        </label>
        <InputText
          id="wizardClassName"
          value={data.name}
          onChange={event => onChange({ name: event.target.value })}
          placeholder="e.g. Math 7 - Section A"
          className={duplicateName ? 'p-invalid' : undefined}
        />
      </div>

      <div className="flex flex-column gap-xs">
        <label htmlFor="wizardClassSection" className="font-bold flex align-items-center gap-xs">
          Period/Section
          <Tooltip target="#wizardSectionInfo" />
          <i
            id="wizardSectionInfo"
            className="nc_icon-nc_info icon-sm"
            data-pr-tooltip={SECTION_TOOLTIP}
            data-pr-position="top"
          />
        </label>
        <InputText
          id="wizardClassSection"
          ref={sectionInputRef}
          value={data.section}
          onChange={event => onChange({ section: event.target.value })}
          placeholder="e.g. Period 2"
          className={duplicateName ? 'p-invalid' : undefined}
        />
        {duplicateName && (
          <span className="text-color-secondary text-sm">Adding a section will make this class unique.</span>
        )}
      </div>

      <div className="grid">
        <div className="col-12 md:col-6 flex flex-column gap-xs">
          <label htmlFor="wizardClassGrade" className="font-bold">
            Grade <span className="nc_required-mark">*</span>
          </label>
          <MultiSelect
            inputId="wizardClassGrade"
            value={data.grades}
            options={grades}
            optionLabel="name"
            dataKey="id"
            onChange={event => onChange({ grades: event.value as Grade[] })}
            placeholder="Select grade"
            display="chip"
            filter={false}
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-6 flex flex-column gap-xs">
          <label htmlFor="wizardClassSite" className="font-bold">
            Site <span className="nc_required-mark">*</span>
          </label>
          <Dropdown
            inputId="wizardClassSite"
            value={data.site}
            options={sites}
            optionLabel="name"
            dataKey="id"
            onChange={event => onChange({ site: event.value as Site })}
            placeholder="Select site"
            className="w-full"
          />
        </div>
      </div>

      {duplicateName && (
        <div className="nc_wizard-warning">
          <p className="m-0 font-semibold">A class with this name already exists.</p>
          <p className="m-0">Please use a different name or add a period or section to continue.</p>
        </div>
      )}

      <span className="text-color-secondary text-sm">
        <span className="nc_required-mark">*</span> Required fields
      </span>
    </div>
  );
};

export default ClassWizardDetailsStep;
